import type { RefObject } from 'react';
import type { FileManagerRef } from 'devextreme-react/file-manager';
import type FileSystemItem from 'devextreme/file_management/file_system_item';
import type { ContextMenuItemClickEvent, ContextMenuShowingEvent, SelectedFileOpenedEvent } from 'devextreme/ui/file_manager';
import { getMediaKind, isPreviewableMedia } from '../mediaViewer/mediaFileTypes';
import type { MediaKind } from '../mediaViewer/mediaFileTypes';
import { listItems } from './fileBrowserApi';
import { buildImagePlaylist, parentDirectoryPath } from '../mediaViewer/mediaGalleryPlaylist';
import type { MediaPreviewState } from '../mediaViewer/MediaPreviewPopup';
import {
  getOfficePreviewKind,
  officeFlagsForItem,
  type DocumentEditorKind
} from '../documentEditor/documentFileTypes';
import type { DocumentPreviewState } from '../documentEditor/DocumentPreviewPopup';
import {
  openDocumentEditorSession,
  openEditorBySurface,
  openMediaPreviewSession,
  openMediaViewerSession,
  openPdfPreviewSession,
  openPdfViewerSession,
  type NavigateFn
} from '../editorShell/editorOpenHelpers';
import { showToast } from '../notifications/toast';
import { confirmAndUnzipArchive } from './fileBrowserUnzip';
import { isPdfFile, isPreviewablePdf } from '../pdfViewer/pdfFileTypes';
import type { PdfPreviewState } from '../pdfViewer/PdfPreviewPopup';
import { parseItemPathForRoutes, type MountRoute } from './createFileBrowserProvider';
import { resolveOpenSurface } from './fileTypeRegistry';
import {
  clipboardPasteActionLabel,
  getClipboardState,
  JARVIS_CLIPBOARD_COPY,
  JARVIS_CUT,
  JARVIS_PASTE,
  canPasteIntoDirectory,
  hasClipboardItems
} from './fileBrowserClipboard';
import { resolveSelectionWriteAccess } from './fileBrowserMountAccess';
import { JARVIS_COPY_TO, JARVIS_MOVE_TO } from './fileBrowserDestination';
import { JARVIS_SHARE } from './fileBrowserShare';
import { resolveMicrosoft365Url } from './fileBrowserApi';
import { isMountUnavailable } from './fileBrowserMountAccess';

export const MEDIA_CONTEXT_PREVIEW = 'jarvisPreviewMedia';
export const MEDIA_CONTEXT_OPEN_IMAGE = 'jarvisOpenImageViewer';
export const MEDIA_CONTEXT_OPEN_VIDEO = 'jarvisOpenVideoViewer';
export const MEDIA_TOOLBAR_PREVIEW = 'jarvisTbPreviewMedia';
export const MEDIA_TOOLBAR_OPEN = 'jarvisTbOpenMediaViewer';

export const DOC_CONTEXT_PREVIEW = 'jarvisPreviewDocument';
export const DOC_CONTEXT_EDIT_EXCEL = 'jarvisEditInExcel';
export const DOC_CONTEXT_EDIT_WORD = 'jarvisEditInWord';
export const DOC_TOOLBAR_PREVIEW = 'jarvisTbPreviewDocument';
export const DOC_TOOLBAR_EDIT_EXCEL = 'jarvisTbEditInExcel';
export const DOC_TOOLBAR_EDIT_WORD = 'jarvisTbEditInWord';

export const PDF_CONTEXT_PREVIEW = 'jarvisPreviewPdf';
export const PDF_CONTEXT_OPEN = 'jarvisOpenPdfViewer';
export const PDF_TOOLBAR_PREVIEW = 'jarvisTbPreviewPdf';
export const PDF_TOOLBAR_OPEN = 'jarvisTbOpenPdfViewer';
export const MICROSOFT_365_OPEN = 'jarvisOpenMicrosoft365';

/** DevExtreme built-in icon for Zip (no native `zip` glyph). */
export const JARVIS_ZIP_ICON = 'packagebox';

export const JARVIS_ZIP = 'jarvisZipSelection';

type ContextMenuEntry =
  | string
  | { name: string; text: string; icon?: string; beginGroup?: boolean; visible?: boolean };

export interface BrowserItemFlags {
  image: boolean;
  video: boolean;
  spreadsheet: boolean;
  richedit: boolean;
  pdf: boolean;
}

export function mediaFlagsForItem(item: FileSystemItem | undefined): BrowserItemFlags {
  if (!item || item.isDirectory) {
    return { image: false, video: false, spreadsheet: false, richedit: false, pdf: false };
  }
  const kind = getMediaKind(item.name);
  const office = officeFlagsForItem(item);
  return {
    image: kind === 'image',
    video: kind === 'video',
    spreadsheet: office.spreadsheet,
    richedit: office.richedit,
    pdf: isPdfFile(item.name)
  };
}

export function buildFileBrowserContextMenuItems(
  canWrite: boolean,
  flags: BrowserItemFlags,
  options: {
    canPaste: boolean;
    canShare: boolean;
    isDirectory: boolean;
    allowOfficeEdit?: boolean;
    /** When false (Public read-only), omit clipboard copy. */
    allowClipboardCopy?: boolean;
    /** When false (unavailable mount), omit Viewer / Workspace actions. */
    allowPreviewOpen?: boolean;
    canOpenMicrosoft365?: boolean;
    allowContentAccess?: boolean;
  }
): ContextMenuEntry[] {
  const allowOfficeEdit = options.allowOfficeEdit ?? true;
  const allowClipboardCopy = options.allowClipboardCopy ?? true;
  const allowPreviewOpen = options.allowPreviewOpen ?? true;
  const allowContentAccess = options.allowContentAccess ?? true;
  const items: ContextMenuEntry[] = [];

  if (allowContentAccess) {
    items.push({ name: JARVIS_COPY_TO, text: 'Copy to', icon: 'copy' });
  }
  if (canWrite) {
    items.push(
      { name: JARVIS_MOVE_TO, text: 'Move to', icon: 'movetofolder' },
      { name: JARVIS_ZIP, text: 'Zip', icon: JARVIS_ZIP_ICON },
      { name: 'rename', text: 'Rename', icon: 'rename', beginGroup: true },
      { name: JARVIS_CUT, text: 'Cut', icon: 'cut' },
      { name: JARVIS_CLIPBOARD_COPY, text: 'Copy', icon: 'copy' },
      {
        name: JARVIS_PASTE,
        text: clipboardPasteActionLabel(getClipboardState()),
        icon: 'paste',
        visible: options.canPaste && hasClipboardItems()
      },
      { name: 'delete', text: 'Delete', icon: 'trash' }
    );
  } else if (allowClipboardCopy) {
    items.push({ name: JARVIS_CLIPBOARD_COPY, text: 'Copy', icon: 'copy' });
    if (options.canPaste && hasClipboardItems()) {
      items.push({
        name: JARVIS_PASTE,
        text: clipboardPasteActionLabel(getClipboardState()),
        icon: 'paste'
      });
    }
  }

  if (allowContentAccess) {
    items.push({ name: 'download', text: 'Download', icon: 'download', beginGroup: true });
  }
  if (allowContentAccess && options.canShare && !options.isDirectory) {
    items.push({ name: JARVIS_SHARE, text: 'Share', icon: 'share' });
  }
  if (options.canOpenMicrosoft365 && !options.isDirectory) {
    items.push({
      name: MICROSOFT_365_OPEN,
      text: 'Open in Microsoft 365',
      icon: 'export',
      beginGroup: true
    });
  }
  if (allowPreviewOpen) {
    if (flags.image || flags.video || flags.pdf) {
      items.push({
        name: flags.pdf ? PDF_CONTEXT_PREVIEW : MEDIA_CONTEXT_PREVIEW,
        text: flags.pdf ? 'Open in Viewer' : 'Preview',
        beginGroup: true,
        visible: true
      });
    }
    if (flags.image) {
      items.push({
        name: MEDIA_CONTEXT_OPEN_IMAGE,
        text: 'Open in Image Viewer',
        visible: true
      });
    }
    if (flags.video) {
      items.push({
        name: MEDIA_CONTEXT_OPEN_VIDEO,
        text: 'Open in Video Viewer',
        visible: true
      });
    }
    if (flags.spreadsheet || flags.richedit) {
      items.push({
        name: DOC_CONTEXT_PREVIEW,
        text: 'Open in Viewer',
        beginGroup: true,
        visible: true
      });
    }
  }
  if (canWrite && flags.spreadsheet && allowOfficeEdit) {
    items.push({ name: DOC_CONTEXT_EDIT_EXCEL, text: 'Open in Editor', visible: true });
  }
  if (canWrite && flags.richedit && allowOfficeEdit) {
    items.push({ name: DOC_CONTEXT_EDIT_WORD, text: 'Open in Editor', visible: true });
  }
  return items;
}

export type OpenDocumentEditorOptions = {
  allowOfficeEdit?: boolean;
  onPreviewInstead?: (state: DocumentPreviewState) => void;
  navigate?: NavigateFn;
};

export function openDocumentEditorTab(
  item: FileSystemItem,
  routes: readonly MountRoute[],
  kind: DocumentEditorKind,
  options?: OpenDocumentEditorOptions
): boolean {
  const parsed = parseItemPathForRoutes(item, routes);
  if (!parsed?.relative) return false;

  if (options?.allowOfficeEdit === false) {
    openDocumentEditorSession({
      kind,
      mountId: parsed.mount.id,
      path: parsed.relative,
      accessMode: 'view',
      preferredMode: 'inAppTab',
      navigate: options.navigate
    });
    showToast('Opened view-only — use a tablet or larger screen to edit.', 'info', 4000);
    return true;
  }

  openDocumentEditorSession({
    kind,
    mountId: parsed.mount.id,
    path: parsed.relative,
    preferredMode: 'inAppTab',
    navigate: options?.navigate
  });
  return true;
}

export function openDocumentPreview(
  item: FileSystemItem,
  routes: readonly MountRoute[],
  setPreview: (state: DocumentPreviewState) => void
): boolean {
  const parsed = parseItemPathForRoutes(item, routes);
  if (!parsed?.relative) return false;
  const kind = getOfficePreviewKind(item.name);
  if (!kind) return false;
  setPreview({
    open: true,
    kind,
    mountId: parsed.mount.id,
    relativePath: parsed.relative
  });
  return true;
}

export function canOpenInMicrosoft365(
  item: FileSystemItem | undefined,
  routes: readonly MountRoute[]
): boolean {
  if (!item || item.isDirectory) return false;
  const parsed = parseItemPathForRoutes(item, routes);
  if (!parsed?.relative || parsed.mount.kind !== 'OneDrive' || isMountUnavailable(parsed.mount)) {
    return false;
  }
  const extension = item.name.split('.').pop()?.toLowerCase();
  return Boolean(extension && MICROSOFT_365_EXTENSIONS.has(extension));
}

const MICROSOFT_365_EXTENSIONS = new Set([
  'doc',
  'docm',
  'docx',
  'dot',
  'dotm',
  'dotx',
  'odp',
  'ods',
  'odt',
  'pot',
  'potm',
  'potx',
  'pps',
  'ppsm',
  'ppsx',
  'ppt',
  'pptm',
  'pptx',
  'rtf',
  'xls',
  'xlsb',
  'xlsm',
  'xlsx',
  'xlt',
  'xltm',
  'xltx'
]);

export async function openInMicrosoft365(
  item: FileSystemItem,
  routes: readonly MountRoute[]
): Promise<boolean> {
  if (!canOpenInMicrosoft365(item, routes)) return false;
  const parsed = parseItemPathForRoutes(item, routes)!;
  try {
    const webUrl = await resolveMicrosoft365Url(parsed.mount.id, parsed.relative);
    window.open(webUrl, '_blank', 'noopener,noreferrer');
    return true;
  } catch (error) {
    showToast(error instanceof Error ? error.message : String(error), 'error', 5000);
    return false;
  }
}

export function resolveMediaTarget(
  item: FileSystemItem,
  routes: readonly MountRoute[]
): { mountId: string; relativePath: string; kind: MediaKind } | null {
  const parsed = parseItemPathForRoutes(item, routes);
  if (!parsed?.relative) return null;
  const kind = getMediaKind(item.name);
  if (!kind) return null;
  return { mountId: parsed.mount.id, relativePath: parsed.relative, kind };
}

export function openMediaPreview(
  item: FileSystemItem,
  routes: readonly MountRoute[],
  setPreview: (state: MediaPreviewState) => void,
  options?: { playlist?: MediaPreviewState['playlist']; navigate?: NavigateFn }
): boolean {
  const target = resolveMediaTarget(item, routes);
  if (!target) return false;

  openMediaPreviewSession({
    kind: target.kind,
    mountId: target.mountId,
    path: target.relativePath,
    navigate: options?.navigate,
    setPreview: (state) => {
      setPreview({
        open: state.open,
        kind: state.kind,
        mountId: state.mountId,
        relativePath: state.relativePath,
        playlist: options?.playlist
      });
    }
  });

  if (target.kind === 'image' && !options?.playlist) {
    void loadImagePlaylistForPreview(target.mountId, target.relativePath).then((playlist) => {
      if (!playlist || playlist.length === 0) return;
      setPreview({
        open: true,
        kind: target.kind,
        mountId: target.mountId,
        relativePath: target.relativePath,
        playlist
      });
    });
  }

  return true;
}

async function loadImagePlaylistForPreview(mountId: string, relativePath: string) {
  const parent = parentDirectoryPath(relativePath);
  const items = await listItems(mountId, parent || undefined);
  return buildImagePlaylist(items);
}

export function openMediaViewerTab(
  item: FileSystemItem,
  routes: readonly MountRoute[],
  kind: MediaKind,
  navigate?: NavigateFn
): boolean {
  const parsed = parseItemPathForRoutes(item, routes);
  if (!parsed?.relative || getMediaKind(item.name) !== kind) return false;
  // Toolbar/context "Open viewer": full viewer inside a Workspace tab, like the editors.
  openMediaViewerSession({
    kind,
    mountId: parsed.mount.id,
    path: parsed.relative,
    navigate,
    preferredMode: 'inAppTab'
  });
  return true;
}

export function openSelectedMediaViewerTab(
  item: FileSystemItem,
  routes: readonly MountRoute[],
  navigate?: NavigateFn
): boolean {
  const kind = getMediaKind(item.name);
  if (!kind) return false;
  return openMediaViewerTab(item, routes, kind, navigate);
}

export function openPdfPreview(
  item: FileSystemItem,
  routes: readonly MountRoute[],
  setPreview: (state: PdfPreviewState) => void,
  navigate?: NavigateFn
): boolean {
  const parsed = parseItemPathForRoutes(item, routes);
  if (!parsed?.relative || !isPreviewablePdf(item.name)) return false;
  openPdfPreviewSession({
    mountId: parsed.mount.id,
    path: parsed.relative,
    navigate,
    setPreview: (state) =>
      setPreview({
        open: state.open,
        mountId: state.mountId,
        relativePath: state.relativePath
      })
  });
  return true;
}

export function openPdfViewerTab(
  item: FileSystemItem,
  routes: readonly MountRoute[],
  navigate?: NavigateFn
): boolean {
  const parsed = parseItemPathForRoutes(item, routes);
  if (!parsed?.relative || !isPreviewablePdf(item.name)) return false;
  // Toolbar/context "Open PDF viewer": full viewer inside a Workspace tab, like the editors.
  openPdfViewerSession({
    mountId: parsed.mount.id,
    path: parsed.relative,
    accessMode:
      parsed.mount.mode === 'ReadWrite' && !isMountUnavailable(parsed.mount) ? 'edit' : 'view',
    navigate,
    preferredMode: 'inAppTab'
  });
  return true;
}

export function handleContextMenuShowing(
  e: ContextMenuShowingEvent,
  _canWrite: boolean,
  fileManagerRef: RefObject<FileManagerRef | null>,
  routes: readonly MountRoute[],
  allowOfficeEdit = true
): void {
  const fm = fileManagerRef.current?.instance();
  if (!fm) return;
  const item = e.fileSystemItem;
  const flags = mediaFlagsForItem(item);
  const destination = fm.getCurrentDirectory?.() ?? null;
  const currentPath = destination?.path ?? '';
  const selected = item ? [item] : [];
  const { canWrite, isUnavailable } = resolveSelectionWriteAccess(currentPath, selected, routes);
  const canPaste = canPasteIntoDirectory(destination, routes, canWrite);
  fm.option(
    'contextMenu.items',
    buildFileBrowserContextMenuItems(canWrite, flags, {
      canPaste,
      canShare: Boolean(item && !item.isDirectory && !isUnavailable),
      canOpenMicrosoft365: canOpenInMicrosoft365(item, routes),
      isDirectory: Boolean(item?.isDirectory),
      allowOfficeEdit,
      allowClipboardCopy: !isUnavailable,
      allowPreviewOpen: !isUnavailable,
      allowContentAccess: !isUnavailable
    })
  );
}

export function handleContextMenuItemClick(
  e: ContextMenuItemClickEvent,
  routes: readonly MountRoute[],
  setMediaPreview: (state: MediaPreviewState) => void,
  setDocumentPreview: (state: DocumentPreviewState) => void,
  setPdfPreview: (state: PdfPreviewState) => void,
  allowOfficeEdit = true,
  navigate?: NavigateFn
): void {
  const item = e.fileSystemItem;
  if (!item || item.isDirectory) return;
  const name = e.itemData?.name;
  if (name === MICROSOFT_365_OPEN) {
    void openInMicrosoft365(item, routes);
  } else if (name === MEDIA_CONTEXT_PREVIEW) {
    openMediaPreview(item, routes, setMediaPreview, { navigate });
  } else if (name === PDF_CONTEXT_PREVIEW) {
    openPdfPreview(item, routes, setPdfPreview, navigate);
  } else if (name === MEDIA_CONTEXT_OPEN_IMAGE) {
    openMediaViewerTab(item, routes, 'image', navigate);
  } else if (name === MEDIA_CONTEXT_OPEN_VIDEO) {
    openMediaViewerTab(item, routes, 'video', navigate);
  } else if (name === PDF_CONTEXT_OPEN) {
    openPdfViewerTab(item, routes, navigate);
  } else if (name === DOC_CONTEXT_PREVIEW) {
    openDocumentPreview(item, routes, setDocumentPreview);
  } else if (name === DOC_CONTEXT_EDIT_EXCEL) {
    openDocumentEditorTab(item, routes, 'spreadsheet', {
      allowOfficeEdit,
      onPreviewInstead: setDocumentPreview,
      navigate
    });
  } else if (name === DOC_CONTEXT_EDIT_WORD) {
    openDocumentEditorTab(item, routes, 'richedit', {
      allowOfficeEdit,
      onPreviewInstead: setDocumentPreview,
      navigate
    });
  }
}

export function handleSelectedFileOpened(
  e: SelectedFileOpenedEvent,
  routes: readonly MountRoute[],
  setMediaPreview: (state: MediaPreviewState) => void,
  fileManagerRef: RefObject<FileManagerRef | null>,
  navigate?: NavigateFn,
  allowOfficeEdit = true
): void {
  const item = e.file;
  // DevExtreme navigates folders internally; only preview files on double-click.
  if (!item || item.isDirectory) return;

  const surface = resolveOpenSurface(item.name);
  const parsed = parseItemPathForRoutes(item, routes);
  const mountCanWrite = parsed?.mount.mode === 'ReadWrite' && !isMountUnavailable(parsed.mount);
  switch (surface) {
    case 'zip':
      void confirmAndUnzipArchive(item, routes, fileManagerRef);
      return;
    case 'media':
      openMediaPreview(item, routes, setMediaPreview, { navigate });
      return;
    case 'pdf':
      openPdfViewerTab(item, routes, navigate);
      return;
    case 'spreadsheet':
    case 'richedit': {
      if (!parsed?.relative) return;
      openDocumentEditorSession({
        kind: surface,
        mountId: parsed.mount.id,
        path: parsed.relative,
        accessMode: mountCanWrite && allowOfficeEdit ? 'edit' : 'view',
        preferredMode: 'inAppTab',
        navigate
      });
      return;
    }
    case 'drawio': {
      if (!parsed?.relative) return;
      openEditorBySurface({
        surface: 'drawio',
        mountId: parsed.mount.id,
        path: parsed.relative,
        accessMode: mountCanWrite ? 'edit' : 'view',
        preferredMode: 'inAppTab',
        navigate
      });
      return;
    }
    case 'markdown':
    case 'monaco':
    case 'code': {
      if (!parsed?.relative) return;
      openEditorBySurface({
        surface,
        mountId: parsed.mount.id,
        path: parsed.relative,
        accessMode: mountCanWrite ? 'edit' : 'view',
        preferredMode: 'inAppTab',
        navigate
      });
      return;
    }
    default:
      return;
  }
}

export function isMediaToolbarPreviewVisible(selectedItems: FileSystemItem[] | undefined): boolean {
  if (!selectedItems || selectedItems.length !== 1) return false;
  const item = selectedItems[0];
  return !item.isDirectory && (isPreviewableMedia(item.name) || isPreviewablePdf(item.name));
}

export function isPdfToolbarPreviewVisible(selectedItems: FileSystemItem[] | undefined): boolean {
  if (!selectedItems || selectedItems.length !== 1) return false;
  const item = selectedItems[0];
  return !item.isDirectory && isPreviewablePdf(item.name);
}
